import { $enum } from "ts-enum-util";

import type { Dayjs } from "@eisox/dayjs";
import { isValveOffline, MECA_PROBLEMS_ENUM } from "@eisox/valves";
import { z } from "@eisox/zod";

import {
  TEMPERATURE_CORRECTION_COEF_LIMITS_MAX,
  TEMPERATURE_CORRECTION_COEF_LIMITS_MIN,
  TEMPERATURE_CORRECTION_COEF_LIMITS_STEP,
} from "~/constants/valve.constant";
import { calculateInterpolatedCorrection } from "~/helpers/correction.helper";
import { calculateEstimatedTemperature } from "~/helpers/valve.helper";
import type { EnhancedValve } from "~/types/valve.type";

export const calibrationSettingsSchema = z.object({
  valves: z.array(
    z.object({
      id: z.string(),
      temperatureCorrectionCoef: z.coerce.number<number>(),
    }),
  ),
});

export type CalibrationSettingsType = z.infer<typeof calibrationSettingsSchema>;

export type CalibrationSeverity =
  | "success"
  | "warning"
  | "heatSourceWarning"
  | "mecaError"
  | "offlineError"
  | "absenceTempError"
  | "correctionError"
  | "error";

export const calculateNewCorrectionCoef = (
  correction: number,
  targetTemp: number,
  sensorTemp: number,
  absenceTemperature: number,
  confortTemperature: number,
): number | undefined => {
  if (correction <= 0) return undefined;
  const interpolatedCorrection = calculateInterpolatedCorrection(
    absenceTemperature,
    confortTemperature,
    sensorTemp,
    correction,
  );
  if (interpolatedCorrection === 0) return undefined;
  const raw = (sensorTemp - targetTemp) / interpolatedCorrection;
  const round = +(
    Math.round(raw / TEMPERATURE_CORRECTION_COEF_LIMITS_STEP) * TEMPERATURE_CORRECTION_COEF_LIMITS_STEP
  ).toFixed(1);
  return Math.min(TEMPERATURE_CORRECTION_COEF_LIMITS_MAX, Math.max(TEMPERATURE_CORRECTION_COEF_LIMITS_MIN, round));
};

export const getCalibrationData = (
  valves: EnhancedValve[],
  absenceTemperature: number,
  confortTemperature: number,
  date: Dayjs,
  setpointTemp?: number,
): { calibrableValves: EnhancedValve[]; severity: CalibrationSeverity } => {
  const MECA_VALUES = $enum(MECA_PROBLEMS_ENUM).getValues();
  const temps: number[] = [];

  for (const v of valves) {
    if (v.errors.some(e => MECA_VALUES.includes(e as MECA_PROBLEMS_ENUM))) {
      return { calibrableValves: [], severity: "mecaError" };
    }
    if (isValveOffline(v.updatedAt, date)) return { calibrableValves: [], severity: "offlineError" };
    if ((v.correction ?? 0) <= 0) return { calibrableValves: [], severity: "correctionError" };

    const temp = calculateEstimatedTemperature(
      absenceTemperature,
      confortTemperature,
      v.sensors?.temperature,
      v.correction,
    );

    if (temp !== undefined) temps.push(temp);
  }

  if (temps.length === 0) return { calibrableValves: valves, severity: "error" };

  const average = temps.reduce((sum, t) => sum + t, 0) / temps.length;

  const severity: CalibrationSeverity =
    setpointTemp !== undefined && average >= setpointTemp + 1
      ? "heatSourceWarning"
      : average >= confortTemperature - 0.5
        ? "success"
        : average >= absenceTemperature + 0.5
          ? "warning"
          : "absenceTempError";

  return { calibrableValves: valves, severity };
};
